import { api } from '@/api';

export const AERIAL_RUNS_TABLE_DOWNLOAD_URL = 'aerial/runs/download';
export const AERIAL_RUN_CASES_TABLE_DOWNLOAD_URL = 'aerial/run/download';

export type AerialTableDownloadScope = 'entire' | 'filtered';

export interface AerialTableSortParams {
  columnKey: string;
  direction: 'asc' | 'desc';
}

export interface AerialTableDownloadRequest {
  aerialId: string;
  runId?: string;
  scope: AerialTableDownloadScope;
  sort: AerialTableSortParams;
  searchTerm?: string;
  columnFilters?: Record<string, string[]>;
  visibleColumns?: string[];
}

export interface AerialTableDownloadResult {
  blob: Blob;
  filename: string;
}

const parseFilenameFromContentDisposition = (header: string | null, fallback: string): string => {
  if (!header) return fallback;

  const utf8Match = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1].trim());
    } catch {
      return fallback;
    }
  }

  const quotedMatch = header.match(/filename="([^"]+)"/i);
  if (quotedMatch?.[1]) return quotedMatch[1].trim();

  const plainMatch = header.match(/filename=([^;]+)/i);
  if (plainMatch?.[1]) return plainMatch[1].trim().replace(/^"|"$/g, '');

  return fallback;
};

const downloadAerialTableApi = api.injectEndpoints({
  endpoints: (builder) => ({
    downloadAerialRunsTable: builder.mutation<AerialTableDownloadResult, AerialTableDownloadRequest>({
      query: (body) => ({
        url: AERIAL_RUNS_TABLE_DOWNLOAD_URL,
        method: 'POST',
        body,
        responseHandler: async (response) => {
          const blob = await response.blob();
          const filename = parseFilenameFromContentDisposition(
            response.headers.get('Content-Disposition'),
            'aerial-runs.xlsx',
          );
          return { blob, filename };
        },
        cache: 'no-cache',
      }),
      extraOptions: { maxRetries: 0 },
    }),
    downloadAerialRunCasesTable: builder.mutation<AerialTableDownloadResult, AerialTableDownloadRequest>({
      query: (body) => ({
        url: AERIAL_RUN_CASES_TABLE_DOWNLOAD_URL,
        method: 'POST',
        body,
        responseHandler: async (response) => {
          const blob = await response.blob();
          const filename = parseFilenameFromContentDisposition(
            response.headers.get('Content-Disposition'),
            'aerial-run-cases.xlsx',
          );
          return { blob, filename };
        },
        cache: 'no-cache',
      }),
      extraOptions: { maxRetries: 0 },
    }),
  }),
});

export const { useDownloadAerialRunsTableMutation, useDownloadAerialRunCasesTableMutation } = downloadAerialTableApi;
